/**
 * StripeConfigPanel — admin tab mirroring PayPalConfigPanel.
 *
 * Three blocks:
 *   1. Stripe-only kill-switch (independent of PayPal)
 *   2. Credential management (publishable / secret / webhook secret) with
 *      Test-connection button — credentials live in db.app_secrets so
 *      they override .env when set.
 *   3. Webhook setup instructions + endpoint URL hint
 */
import React, { useEffect, useState } from "react";
import { Save, X, Copy, Check, ShieldCheck, AlertCircle, ExternalLink, CreditCard, Power, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useContentApi } from "../../context/ContentContext";

const StripeConfigPanel = () => {
    const [config, setConfig] = useState(null);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    useEffect(() => { reloadConfig(); }, []);
    const reloadConfig = () => {
        api.get("/stripe/admin/config")
            .then((r) => setConfig(r.data))
            .catch(() => toast.error("Could not load Stripe config"));
    };

    const testConn = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const { data } = await api.post("/stripe/admin/config/test");
            setTestResult(data);
            if (data.ok) toast.success(`Stripe connected (${data.mode})`);
            else toast.error(data.detail || "Test failed");
        } catch (e) {
            const msg = e?.response?.data?.detail || "Test failed";
            setTestResult({ ok: false, detail: msg });
            toast.error(msg);
        } finally { setTesting(false); }
    };

    if (!config) return <p className="text-sm text-[#4A5568]">Loading Stripe config…</p>;

    return (
        <div className="space-y-6" data-testid="stripe-config-panel">
            <div>
                <h2 className="font-heading text-2xl text-[#1C252E] tracking-tight">Stripe configuration</h2>
                <p className="text-sm text-[#4A5568] mt-1 max-w-2xl">Manage Stripe Elements credentials. Used for card / Apple Pay / Google Pay / Cash App / Link and monthly recurring donations. Independent of PayPal — flip either off without affecting the other.</p>
            </div>

            <StripeKillSwitchBlock />

            {/* Connection status */}
            <div className="bg-white border border-black/10 rounded-2xl p-5 space-y-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-start gap-3">
                        <CreditCard className="w-5 h-5 text-[#635BFF] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                        <div>
                            <p className="font-medium text-[#1C252E]">Connection</p>
                            <p className="text-xs text-[#4A5568] mt-1">
                                Mode: <span className={`font-bold uppercase tracking-wider ${config.mode === "live" ? "text-[#E53935]" : "text-amber-700"}`}>{config.mode}</span>
                                {" · "}
                                Publishable key: {config.publishable_key_set ? <code className="text-xs">…{config.publishable_key_last4}</code> : <span className="italic text-[#E53935]">missing</span>}
                                {" · "}
                                Secret key: {config.secret_key_set ? <code className="text-xs">…{config.secret_key_last4}</code> : <span className="italic text-[#E53935]">missing</span>}
                                {" · "}
                                Webhook secret: {config.webhook_secret_set ? <span className="text-green-700">set</span> : <span className="italic text-amber-700">not yet</span>}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={testConn}
                        disabled={testing}
                        data-testid="stripe-test-btn"
                        className="px-4 py-2 rounded-full text-sm font-medium border border-black/10 text-[#1C252E] hover:border-[#635BFF] disabled:opacity-60"
                    >
                        {testing ? "Testing…" : "Test connection"}
                    </button>
                </div>
                {testResult && (
                    <div className={`text-xs rounded-lg p-3 ${testResult.ok ? "bg-green-50 text-green-800" : "bg-red-50 text-[#E53935]"}`} data-testid="stripe-test-result">
                        {testResult.ok
                            ? `✅ Stripe ${testResult.mode} mode · available balance ${(testResult.available_cents / 100).toFixed(2)} ${(testResult.currency || "").toUpperCase()}`
                            : `❌ ${testResult.detail || "Connection failed"}`}
                    </div>
                )}
            </div>

            {/* Credentials editor */}
            <StripeCredentialRow keyName="STRIPE_PUBLISHABLE_KEY" label="Publishable key" placeholder="pk_test_… or pk_live_…" onSaved={reloadConfig} />
            <StripeCredentialRow keyName="STRIPE_API_KEY" label="Secret key" placeholder="sk_test_… or sk_live_…" isSecret onSaved={reloadConfig} />
            <StripeCredentialRow keyName="STRIPE_WEBHOOK_SECRET" label="Webhook signing secret" placeholder="whsec_…" isSecret onSaved={reloadConfig} />

            {/* Webhook log diagnostic */}
            <StripeWebhookLogPanel />

            {/* Webhook setup hint */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
                <p className="text-sm text-amber-900 font-bold mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" strokeWidth={2} /> Webhook setup (one-time)
                </p>
                <ol className="text-xs text-amber-900 leading-relaxed list-decimal pl-5 space-y-1">
                    <li>Go to <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-1">Stripe Dashboard → Developers → Webhooks <ExternalLink className="w-3 h-3" /></a></li>
                    <li>Click <strong>Add endpoint</strong>. URL: <code className="bg-white px-1 py-0.5 rounded text-[10px]">{(typeof window !== "undefined" ? window.location.origin : "https://your-domain") + "/api/stripe/webhook"}</code></li>
                    <li>Select events: <code className="text-[10px]">payment_intent.succeeded</code>, <code className="text-[10px]">invoice.paid</code>, <code className="text-[10px]">customer.subscription.deleted</code>, <code className="text-[10px]">charge.refunded</code></li>
                    <li>Copy the <strong>Signing secret</strong> (starts with <code>whsec_</code>) and paste it above.</li>
                </ol>
            </div>
        </div>
    );
};

const StripeKillSwitchBlock = () => {
    const { c, saveContent, loaded } = useContentApi();
    const enabled = c("stripe.disabled", "false") === "true";
    const [saving, setSaving] = useState(false);

    const toggle = async () => {
        setSaving(true);
        try {
            await saveContent("stripe.disabled", enabled ? "false" : "true");
            toast.success(enabled ? "Stripe card payments re-enabled" : "Stripe DISABLED (PayPal still active)");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save");
        } finally { setSaving(false); }
    };

    return (
        <div
            data-testid="stripe-killswitch-block"
            className={`border rounded-2xl p-5 ${enabled ? "bg-amber-50 border-amber-300" : "bg-white border-black/10"}`}
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-start gap-3">
                    <Power className={`w-5 h-5 mt-0.5 flex-shrink-0 ${enabled ? "text-amber-700" : "text-[#635BFF]"}`} strokeWidth={2} />
                    <div>
                        <p className="font-medium text-[#1C252E]">Disable Stripe card payments only</p>
                        <p className="text-xs text-[#4A5568] mt-1 max-w-2xl leading-relaxed">
                            Hides only the Stripe Elements form across the site (Donate, Shop, Raffle). <strong>PayPal & Venmo stay live.</strong> Flip this DURING a Stripe key rotation, account review, or any time Stripe&apos;s status page reports an outage.
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={toggle}
                    disabled={saving || !loaded}
                    data-testid="stripe-killswitch-toggle"
                    className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors flex-shrink-0 ${enabled ? "bg-amber-500" : "bg-[#635BFF]"}`}
                >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${enabled ? "translate-x-8" : "translate-x-1"}`} />
                </button>
            </div>
        </div>
    );
};

// Reusable inline credential editor — writes through /api/admin/app-secrets/{KEY}.
// Doesn't display the current value (which the backend never returns); shows
// "set" / "missing" via the parent panel's config snapshot.
const StripeCredentialRow = ({ keyName, label, placeholder, isSecret, onSaved }) => {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState("");
    const [show, setShow] = useState(false);
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (!value.trim()) { toast.error("Value cannot be empty"); return; }
        setSaving(true);
        try {
            await api.put(`/admin/app-secrets/${keyName}`, { value: value.trim() });
            toast.success(`${label} saved`);
            setEditing(false);
            setValue("");
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save");
        } finally { setSaving(false); }
    };

    const clear = async () => {
        if (!window.confirm(`Remove ${label}? Stripe payments will stop working until you re-add it.`)) return;
        try {
            await api.delete(`/admin/app-secrets/${keyName}`);
            toast.success(`${label} cleared`);
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not clear");
        }
    };

    return (
        <div className="bg-white border border-black/10 rounded-2xl p-5" data-testid={`stripe-cred-${keyName.toLowerCase()}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
                <div>
                    <p className="font-medium text-[#1C252E]">{label}</p>
                    <p className="text-[10px] uppercase tracking-wider font-bold text-[#4A5568] mt-1">{keyName}</p>
                </div>
                {!editing ? (
                    <div className="flex gap-2">
                        <button onClick={() => setEditing(true)} className="text-sm text-[#635BFF] hover:underline" data-testid={`stripe-cred-edit-${keyName.toLowerCase()}`}>Set / change</button>
                        <button onClick={clear} className="text-sm text-[#E53935] hover:underline">Clear</button>
                    </div>
                ) : (
                    <button onClick={() => { setEditing(false); setValue(""); }} className="text-sm text-[#4A5568] hover:underline">Cancel</button>
                )}
            </div>
            {editing && (
                <div className="flex gap-2 items-center">
                    <div className="relative flex-1">
                        <input
                            type={isSecret && !show ? "password" : "text"}
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            placeholder={placeholder}
                            data-testid={`stripe-cred-input-${keyName.toLowerCase()}`}
                            className="w-full border border-black/10 rounded-xl px-4 py-2.5 pr-10 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#635BFF]/40"
                        />
                        {isSecret && (
                            <button
                                type="button"
                                onClick={() => setShow((s) => !s)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A5568] hover:text-[#1C252E]"
                            >
                                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        )}
                    </div>
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid={`stripe-cred-save-${keyName.toLowerCase()}`}
                        className="inline-flex items-center gap-1 px-4 py-2.5 rounded-full text-sm font-medium bg-[#635BFF] hover:bg-[#4E47CC] text-white disabled:opacity-60"
                    >
                        <Save className="w-4 h-4" />
                        {saving ? "Saving…" : "Save"}
                    </button>
                </div>
            )}
        </div>
    );
};

export default StripeConfigPanel;

const StripeWebhookLogPanel = () => {
    const [log, setLog] = useState(null);
    const [diag, setDiag] = useState(null);
    const [loading, setLoading] = useState(false);
    const [diagnosing, setDiagnosing] = useState(false);
    const [reconciling, setReconciling] = useState(false);
    const [reconcileResult, setReconcileResult] = useState(null);
    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/stripe/admin/webhook-log?limit=20");
            setLog(data);
        } catch (e) {
            // silent — non-critical
        } finally { setLoading(false); }
    };
    const runDiagnose = async () => {
        setDiagnosing(true);
        try {
            const { data } = await api.get("/stripe/admin/webhook-diagnose");
            setDiag(data);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Diagnostic failed");
        } finally { setDiagnosing(false); }
    };
    const reconcileOrphans = async () => {
        if (!window.confirm("Pull every succeeded-but-unattributed Stripe intent and re-run attribution? This will retroactively post donation/order records + journal entries for any payment that missed its webhook. Safe and idempotent.")) return;
        setReconciling(true);
        try {
            const { data } = await api.post("/stripe/admin/resync-orphans");
            setReconcileResult(data);
            toast.success(`Reconciled ${data.reconciled} of ${data.scanned} orphan intent(s)`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Reconcile failed");
        } finally { setReconciling(false); }
    };
    useEffect(() => { load(); }, []);

    if (!log) return null;
    // Stale = webhooks worked once but haven't arrived in > 3 days. Louder red
    // than "never received any" because it usually means the endpoint URL in
    // Stripe drifted, or Stripe auto-disabled after N consecutive 5xx replies.
    const stale = log.total_received > 0 && log.days_since_last != null && log.days_since_last > 3;
    const healthy = (log.total_received || 0) > 0 && log.total_verified === log.total_received && !stale;
    return (
        <div className="bg-white border border-black/10 rounded-2xl p-5" data-testid="stripe-webhook-log-panel">
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div>
                    <p className="font-medium text-[#1C252E] flex items-center gap-2">
                        {healthy ? <ShieldCheck className="w-4 h-4 text-green-700" /> : <AlertCircle className={`w-4 h-4 ${stale ? "text-red-700" : "text-amber-700"}`} />}
                        Recent Stripe webhooks
                    </p>
                    <p className="text-xs text-[#4A5568] mt-1">
                        {log.total_received} total received · {log.total_verified} signature-verified
                        {log.last_received_at && <> · last {new Date(log.last_received_at).toLocaleString()}</>}
                    </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button onClick={runDiagnose} disabled={diagnosing} className="text-xs px-3 py-1.5 rounded-full bg-[#635BFF] text-white hover:bg-[#4d47d1] disabled:opacity-50" data-testid="stripe-webhook-diagnose-btn">
                        {diagnosing ? "Checking Stripe…" : "Diagnose in Stripe"}
                    </button>
                    <button onClick={load} disabled={loading} className="text-xs text-[#635BFF] hover:underline" data-testid="stripe-webhook-log-refresh">
                        {loading ? "Refreshing…" : "Refresh"}
                    </button>
                </div>
            </div>

            {stale && (
                <div className="text-xs text-red-800 bg-red-50 border border-red-300 rounded-lg p-3 mb-3" data-testid="stripe-webhook-stale-warning">
                    <p className="font-semibold mb-1">⚠ Webhooks stopped arriving {Math.floor(log.days_since_last)} days ago.</p>
                    <p className="mb-2">
                        Webhooks WERE working (last one: {new Date(log.last_received_at).toLocaleString()}) but haven't fired since.
                        {log.unattributed_intents > 0 && <> There are <strong>{log.unattributed_intents} succeeded payment intents</strong> on our side that never got a matching webhook — those donations may be stuck as "Webhook pending".</>}
                    </p>
                    <p className="font-semibold">Most common causes:</p>
                    <ol className="list-decimal ml-5 mt-1 space-y-0.5">
                        <li>The endpoint URL in Stripe dashboard doesn't match our current URL (preview URL drift)</li>
                        <li>Stripe auto-disabled the endpoint after repeated 5xx failures</li>
                        <li>The signing secret was rotated in Stripe but not in our app_secrets</li>
                    </ol>
                    <p className="mt-2">Click <strong>Diagnose in Stripe</strong> to fetch the live endpoint config from Stripe's API.</p>
                    {log.unattributed_intents > 0 && (
                        <div className="mt-3 pt-3 border-t border-red-200">
                            <p className="font-semibold mb-1">Fix orphan payments retroactively:</p>
                            <p className="mb-2">The tool below pulls each stuck intent from Stripe and re-runs attribution — safely posts the missing donation/order + journal entry. Idempotent.</p>
                            <button
                                onClick={reconcileOrphans}
                                disabled={reconciling}
                                data-testid="stripe-reconcile-orphans-btn"
                                className="text-xs px-4 py-2 rounded-full bg-red-700 text-white hover:bg-red-800 disabled:opacity-50 font-medium"
                            >
                                {reconciling ? "Reconciling…" : `Reconcile ${log.unattributed_intents} orphan intent${log.unattributed_intents === 1 ? "" : "s"} now`}
                            </button>
                        </div>
                    )}
                </div>
            )}

            {reconcileResult && (
                <div className="text-xs bg-green-50 border border-green-300 rounded-lg p-3 mb-3" data-testid="stripe-reconcile-result">
                    <p className="font-semibold text-green-900 mb-2">
                        Reconcile complete — {reconcileResult.reconciled} of {reconcileResult.scanned} intent(s) attributed
                        {reconcileResult.errors > 0 && <> · <span className="text-red-700">{reconcileResult.errors} error(s)</span></>}
                        {reconcileResult.skipped > 0 && <> · {reconcileResult.skipped} skipped</>}
                    </p>
                    <details>
                        <summary className="cursor-pointer text-green-900 font-medium">See per-intent log</summary>
                        <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                            {reconcileResult.log.map((row, i) => (
                                <li key={i} className="font-mono text-[10px] flex gap-2">
                                    <span className={`inline-block w-16 font-semibold ${row.result === "reconciled" ? "text-green-700" : row.result === "skipped" ? "text-slate-500" : "text-red-700"}`}>{row.result}</span>
                                    <span className="flex-1 break-all">{row.payment_intent_id || "—"}{row.detail ? ` · ${row.detail}` : ""}{row.amount_usd ? ` · $${row.amount_usd}` : ""}</span>
                                </li>
                            ))}
                        </ul>
                    </details>
                </div>
            )}

            {log.total_received === 0 && (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3" data-testid="stripe-webhook-empty-warning">
                    <strong>No webhooks received yet.</strong> Verify the webhook endpoint URL in Stripe Dashboard → Webhooks matches{" "}
                    <code className="bg-white px-1 py-0.5 rounded">{(typeof window !== "undefined" ? window.location.origin : "")}/api/stripe/webhook</code>, the signing secret above matches the one Stripe shows, and at least <code>payment_intent.succeeded</code> + <code>invoice.paid</code> are subscribed.
                </p>
            )}

            {diag && (
                <div className="text-xs bg-[#F4F1EA] border border-black/10 rounded-lg p-3 mb-3 space-y-2" data-testid="stripe-webhook-diagnose-result">
                    <p className="font-semibold text-[#1C252E]">Stripe-side webhook config</p>
                    <p className="text-[11px] text-[#4A5568]">
                        Expected URL:&nbsp;
                        <code className="bg-white px-1 py-0.5 rounded font-mono">{diag.expected_url || "(PUBLIC_BACKEND_URL env var not set)"}</code>
                    </p>
                    {!diag.ok && <p className="text-red-700">Error: {diag.error}</p>}
                    {diag.ok && diag.endpoints?.length === 0 && (
                        <p className="text-red-700 font-semibold">❌ No webhook endpoints are configured in Stripe at all. Add one at{" "}
                            <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noreferrer" className="underline">dashboard.stripe.com/webhooks</a>
                        </p>
                    )}
                    {diag.ok && diag.endpoints?.map((ep) => (
                        <div key={ep.id} className={`p-2 rounded border ${ep.matches_expected && ep.status === "enabled" ? "bg-green-50 border-green-300" : "bg-red-50 border-red-300"}`}>
                            <p className="font-mono text-[10px] break-all">{ep.url}</p>
                            <div className="mt-1 flex flex-wrap gap-1 text-[10px]">
                                <span className={`px-2 py-0.5 rounded-full font-semibold ${ep.status === "enabled" ? "bg-green-200 text-green-800" : "bg-red-200 text-red-800"}`}>
                                    {ep.status}
                                </span>
                                <span className={`px-2 py-0.5 rounded-full font-semibold ${ep.matches_expected ? "bg-green-200 text-green-800" : "bg-red-200 text-red-800"}`}>
                                    {ep.matches_expected ? "URL matches ✓" : "URL mismatch ✗"}
                                </span>
                                <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">{ep.livemode ? "live" : "test"}</span>
                                <span className="px-2 py-0.5 rounded-full bg-slate-200 text-slate-700">{ep.enabled_events.length} events</span>
                            </div>
                            {!ep.matches_expected && (
                                <p className="text-[10px] text-red-800 mt-1">→ Edit this endpoint in Stripe dashboard and change the URL to match our expected URL above.</p>
                            )}
                            {ep.status !== "enabled" && (
                                <p className="text-[10px] text-red-800 mt-1">→ This endpoint is <strong>disabled</strong>. Re-enable it in Stripe dashboard.</p>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {log.rows?.length > 0 && (
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="bg-[#F4F1EA] text-[10px] uppercase tracking-wider text-[#4A5568]">
                            <tr>
                                <th className="text-left px-3 py-2">Event type</th>
                                <th className="text-left px-3 py-2">Data ID</th>
                                <th className="text-left px-3 py-2">Verified</th>
                                <th className="text-left px-3 py-2">Received</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-black/5">
                            {log.rows.slice(0, 10).map((r) => (
                                <tr key={r.id} data-testid={`stripe-webhook-row-${r.id}`}>
                                    <td className="px-3 py-2 font-mono">{r.event_type}</td>
                                    <td className="px-3 py-2 text-[#4A5568]">{r.data_id?.slice(0, 24) || "—"}</td>
                                    <td className="px-3 py-2">{r.verified ? <span className="text-green-700">✓</span> : <span className="text-amber-700">unverified</span>}</td>
                                    <td className="px-3 py-2 text-[#4A5568]">{new Date(r.received_at).toLocaleTimeString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

